import React, { useEffect, useRef, useState } from "react";
import useSubscriptionStore from "../../../store/builders/useSubscriptionStore";
import useProfileStore from "../../../store/builders/useProfileStore";
import { Loader2, AlertCircle, Download } from "lucide-react";
import { usePDF } from "react-to-pdf";
import InvoiceTemplate from "../../../components/InvoiceTemplate";
import toast from "react-hot-toast";

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatString = (str) => {
  if (!str) return 'N/A';
  return str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

const statusClasses = {
  active: {
    bg: "bg-green-100",
    text: "text-green-700",
    dot: "bg-green-500",
  },
  pending: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    dot: "bg-yellow-500",
  },
  failed: {
    bg: "bg-red-100",
    text: "text-red-700",
    dot: "bg-red-500",
  },
  expired: {
    bg: "bg-gray-100",
    text: "text-gray-700",
    dot: "bg-gray-500",
  },
};

const SubscriptionHistoryTile = () => {
  const { history, isLoadingHistory, errorHistory, fetchSubscriptionHistory } = useSubscriptionStore();
  const { profile, fetchProfile } = useProfileStore();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { toPDF, targetRef } = usePDF({
    filename: `invoice-${selectedInvoice?.invoiceNumber || 'ducktail'}.pdf`,
    page: { margin: 10 }
  });

  useEffect(() => {
    fetchSubscriptionHistory();
    if (!profile) {
      fetchProfile();
    }
  }, [fetchSubscriptionHistory, fetchProfile, profile]);

  const handleDownloadInvoice = (transaction) => {
    // Calculate GST breakdown
    const GST_RATE = 0.18;
    const baseAmount = parseFloat(transaction.amount);
    const cgst = baseAmount * 0.09;
    const sgst = baseAmount * 0.09;
    const totalAmount = baseAmount + cgst + sgst;

    const invoiceData = {
      transaction,
      invoiceNumber: transaction.merchant_order_id || transaction.cashfree_order_id || `INV-${transaction.id}`,
      invoiceDate: transaction.created_at,
      amount: baseAmount,
      cgst,
      sgst,
      totalAmount,
    };

    setSelectedInvoice(invoiceData);

    // Wait for the component to render before generating PDF
    setTimeout(() => {
      toPDF()
        .then(() => {
          toast.success('Invoice downloaded successfully!');
          setSelectedInvoice(null);
        })
        .catch((error) => {
          console.error('Error generating PDF:', error);
          toast.error('Failed to generate invoice. Please try again.');
          setSelectedInvoice(null);
        });
    }, 100);
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 w-full">
        <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4">Transaction History</h2>

        {isLoadingHistory ? (
          <div className="flex justify-center items-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <p className="ml-3 text-gray-600">Loading history...</p>
          </div>
        ) : errorHistory ? (
          <div className="text-center py-10 text-red-600 bg-red-50 p-4 rounded-lg">
            <AlertCircle className="w-12 h-12 mx-auto mb-2" />
            <p className="font-semibold">Error</p>
            <p className="text-sm">{errorHistory}</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <p className="font-semibold">No Subscription History Found</p>
            <p className="text-sm">Your past transactions will appear here.</p>
          </div>
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="text-center text-xs font-medium text-gray-500 border-b">
                    <th className="py-2 px-4">Time & Date</th>
                    <th className="py-2 px-4">Transaction ID</th>
                    <th className="py-2 px-4">Paid Amount</th>
                    <th className="py-2 px-4">Type</th>
                    <th className="py-2 px-4">Status</th>
                    <th className="py-2 px-4">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((transaction) => {
                    const statusStyle = statusClasses[transaction.status.toLowerCase()] || statusClasses.expired;
                    // Calculate Total Amount (Base + 18% GST)
                    const baseAmount = parseFloat(transaction.amount || 0);
                    const totalAmount = baseAmount * 1.18;

                    return (
                      <tr
                        key={transaction.id}
                        className="text-center text-xs text-gray-700 border-b hover:bg-gray-50"
                      >
                        <td className="py-3 px-4">{formatDateTime(transaction.created_at)}</td>
                        <td className="py-3 px-4 font-mono">{transaction.phonepe_transaction_id || transaction.cashfree_order_id || transaction.merchant_order_id || 'N/A'}</td>
                        <td className="py-3 px-4">{formatCurrency(totalAmount)}</td>
                        <td className="py-3 px-4">{formatString(transaction.type)}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
                          >
                            <span
                              className={`h-2 w-2 rounded-full ${statusStyle.dot} mr-2`}
                            ></span>
                            {formatString(transaction.status)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleDownloadInvoice(transaction)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            title="Download Invoice"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden flex flex-col gap-4">
              {history.map((transaction) => {
                const statusStyle = statusClasses[transaction.status.toLowerCase()] || statusClasses.expired;
                // Calculate Total Amount (Base + 18% GST)
                const baseAmount = parseFloat(transaction.amount || 0);
                const totalAmount = baseAmount * 1.18;

                return (
                  <div
                    key={transaction.id}
                    className="border rounded-xl p-3 shadow-sm bg-gray-50 flex flex-col gap-2"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Time & Date</span>
                      <span className="text-xs text-gray-700">{formatDateTime(transaction.created_at)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Transaction ID</span>
                      <span className="text-xs text-gray-700 font-mono truncate max-w-[120px]">{transaction.phonepe_transaction_id || transaction.cashfree_order_id || transaction.merchant_order_id || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Paid Amount</span>
                      <span className="text-xs text-gray-700 font-semibold">{formatCurrency(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Type</span>
                      <span className="text-xs text-gray-700">{formatString(transaction.type)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Status</span>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${statusStyle.dot} mr-2`}
                        ></span>
                        {formatString(transaction.status)}
                      </span>
                    </div>
                    <div className="flex justify-center mt-3">
                      <button
                        onClick={() => handleDownloadInvoice(transaction)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                      >
                        <Download className="w-4 h-4" />
                        Download Invoice
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Hidden Invoice Template for PDF Generation */}
      {selectedInvoice && (
        <div className="fixed left-[-9999px] top-0">
          <InvoiceTemplate
            ref={targetRef}
            invoiceData={selectedInvoice}
            profile={profile}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionHistoryTile;